import {
  useRef, useState, useCallback, useEffect,
} from 'react';
import {
  Paper, Box, Typography, IconButton, Chip, Collapse, Tooltip, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useWidgetDockState } from '../../common/components/WidgetDockContext';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';

// Altura aproximada de cada item (linha) no painel
const ITEM_HEIGHT = 36;
// Número máximo de itens visíveis antes do scroll
const MAX_VISIBLE_ITEMS = 6;
// Altura máxima do conteúdo (6 itens + padding)
const MAX_CONTENT_HEIGHT = ITEM_HEIGHT * MAX_VISIBLE_ITEMS + 16;

const useStyles = makeStyles()((theme, { tokens }) => ({
  // 2ECOMP Premium: Container principal com glassmorphism
  root: {
    position: 'absolute',
    zIndex: 1000,
    width: 220,
    maxWidth: 'calc(100vw - 60px)',
    borderRadius: 16,
    overflow: 'hidden',
    userSelect: 'none',
    // Glassmorphism medium - explicit properties
    backgroundColor: 'rgba(22, 27, 34, 0.75)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    border: `1px solid ${tokens?.border?.subtle || 'rgba(255, 255, 255, 0.1)'}`,
    boxShadow: `${tokens?.shadows?.lg || '0 8px 24px rgba(0, 0, 0, 0.5)'}, ${tokens?.shadows?.inset || 'inset 0 1px 0 rgba(255, 255, 255, 0.05)'}`,
    transition: 'box-shadow 0.2s ease, transform 0.15s ease',
    // Borda accent no topo
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 16,
      right: 16,
      height: 2,
      background: `linear-gradient(90deg, transparent, ${tokens.accent.primary}, transparent)`,
      borderRadius: '0 0 2px 2px',
      zIndex: 1,
    },
    '&:hover': {
      boxShadow: `${tokens.shadows.xl}, ${tokens.shadows.inset}`,
    },
    // Tablet
    [theme.breakpoints.down('md')]: {
      width: 200,
    },
    // Mobile
    [theme.breakpoints.down('sm')]: {
      width: 180,
      maxWidth: 'calc(100vw - 80px)',
      borderRadius: 12,
    },
  },
  rootDragging: {
    boxShadow: `${tokens.shadows.xl}, 0 0 20px ${tokens.accent.glow}`,
    transform: 'scale(1.02)',
    opacity: 0.95,
  },
  rootCollapsed: {
    width: 'auto',
    minWidth: 140,
  },
  // Header com drag handle
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0.75, 1.25),
    backgroundColor: tokens.surface.glass,
    borderBottom: `1px solid ${tokens.border.subtle}`,
    cursor: 'grab',
    minHeight: 28,
    '&:active': {
      cursor: 'grabbing',
    },
  },
  headerCollapsed: {
    borderBottom: 'none',
    borderRadius: 16,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    flex: 1,
    minWidth: 0,
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    minWidth: 0,
  },
  titleText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: tokens.text.secondary,
  },
  dragHandle: {
    color: tokens.text.muted,
    cursor: 'grab',
    fontSize: 14,
    opacity: 0.5,
    '&:hover': {
      color: tokens.accent.primary,
      opacity: 1,
    },
    '&:active': {
      cursor: 'grabbing',
    },
  },
  // Content area
  content: {
    maxHeight: MAX_CONTENT_HEIGHT,
    overflowY: 'auto',
    overflowX: 'hidden',
    '&::-webkit-scrollbar': {
      width: 5,
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: tokens.border.default,
      borderRadius: 3,
      '&:hover': {
        background: tokens.text.muted,
      },
    },
  },
  // Count chip premium
  countChip: {
    minWidth: 36,
    height: 20,
    fontSize: '0.7rem',
    fontWeight: 700,
    '& .MuiChip-label': {
      padding: '0 6px',
    },
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
  },
  iconButton: {
    padding: 4,
    borderRadius: 4,
    color: tokens.text.muted,
    '& svg': {
      fontSize: 14,
    },
    '&:hover': {
      backgroundColor: tokens.surface.glassHover,
      color: tokens.text.primary,
    },
  },
  collapseButton: {
    '&:hover': {
      color: tokens.accent.primary,
    },
  },
  closeButton: {
    '&:hover': {
      backgroundColor: `${tokens.status.alarm}20`,
      color: tokens.status.alarm,
    },
  },
  // Mobile styles
  rootMobile: {
    position: 'fixed',
    bottom: 'auto',
    left: 'auto',
  },
}));

/**
 * Painel de filtro arrastável premium com responsividade
 * Suporta dock no TopBar quando minimizado
 */
const DraggableFilterPanel = ({
  panelId = 'filter-panel',
  widgetId, // ID para integração com dock (ex: 'poiFilter', 'geofenceFilter')
  open,
  onClose,
  title,
  icon,
  count,
  countColor = 'primary',
  initialPosition = { top: 10, right: 50 },
  children,
}) => {
  const tokens = usePremiumTokens();
  const { classes, cx } = useStyles({ tokens });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const panelRef = useRef(null);
  const storageKey = `filterPanel_${panelId}`;

  // Dock state management (only if widgetId is provided)
  const dockState = widgetId ? useWidgetDockState(widgetId) : null;
  const { isDocked, isMinimized, toggleMinimize, updateData } = dockState || {};

  // Estados
  const [position, setPosition] = useState(initialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem(`${storageKey}_collapsed`);
      return saved === 'true';
    } catch {
      return false;
    }
  });

  const dragOffset = useRef({ x: 0, y: 0 });
  const wasOpen = useRef(false);

  // Reset position when panel opens (manter comportamento lado a lado)
  useEffect(() => {
    if (open && !wasOpen.current) {
      setPosition(initialPosition);
    }
    wasOpen.current = open;
  }, [open, initialPosition]);

  // Persistir estado collapsed
  useEffect(() => {
    try {
      localStorage.setItem(`${storageKey}_collapsed`, String(isCollapsed));
    } catch {
      // Ignore storage errors
    }
  }, [isCollapsed, storageKey]);

  // Sync local isCollapsed with dock isMinimized
  // When widget is expanded from TopBar dock, reset isCollapsed
  useEffect(() => {
    if (isMinimized === false && isCollapsed) {
      setIsCollapsed(false);
    }
  }, [isMinimized, isCollapsed]);

  // Update dock chip data when count changes
  // Note: updateData is intentionally excluded from deps to avoid infinite loops
  // as it's a stable callback from context
  useEffect(() => {
    if (updateData && count !== undefined) {
      updateData({ value: count, label: title });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count, title]);

  const handleMouseDown = useCallback((e) => {
    if (e.target.closest('button') || e.target.closest('input') || e.target.closest('.MuiCheckbox-root')) {
      return;
    }

    setIsDragging(true);
    const panel = panelRef.current;
    if (panel) {
      const rect = panel.getBoundingClientRect();
      dragOffset.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    }
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;

    const newLeft = e.clientX - dragOffset.current.x;
    const newTop = e.clientY - dragOffset.current.y;

    const maxX = window.innerWidth - (panelRef.current?.offsetWidth || 220);
    const maxY = window.innerHeight - (panelRef.current?.offsetHeight || 200);

    setPosition({
      left: Math.max(0, Math.min(newLeft, maxX)),
      top: Math.max(0, Math.min(newTop, maxY)),
      right: 'auto',
      bottom: 'auto',
    });
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleToggleCollapse = useCallback(() => {
    setIsCollapsed((prev) => {
      const newValue = !prev;
      // When using dock system, sync minimize state
      if (toggleMinimize && newValue) {
        toggleMinimize();
      }
      return newValue;
    });
  }, [toggleMinimize]);

  // Touch events para mobile
  const handleTouchStart = useCallback((e) => {
    if (e.target.closest('button') || e.target.closest('input') || e.target.closest('.MuiCheckbox-root')) {
      return;
    }

    const touch = e.touches[0];
    setIsDragging(true);
    const panel = panelRef.current;
    if (panel) {
      const rect = panel.getBoundingClientRect();
      dragOffset.current = {
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top,
      };
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;

    const touch = e.touches[0];
    const newLeft = touch.clientX - dragOffset.current.x;
    const newTop = touch.clientY - dragOffset.current.y;

    const maxX = window.innerWidth - (panelRef.current?.offsetWidth || 220);
    const maxY = window.innerHeight - (panelRef.current?.offsetHeight || 200);

    setPosition({
      left: Math.max(0, Math.min(newLeft, maxX)),
      top: Math.max(0, Math.min(newTop, maxY)),
      right: 'auto',
      bottom: 'auto',
    });
  }, [isDragging]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Event listeners para drag
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
    return undefined;
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  // When minimized, TopBar renders the chip - always go to dock bar
  // No floating minimized view - user clicks chip in TopBar to restore
  if (isMinimized) {
    return null;
  }

  if (!open) return null;

  return (
    <Paper
      ref={panelRef}
      className={cx(
        classes.root,
        isDragging && classes.rootDragging,
        isCollapsed && classes.rootCollapsed,
        isMobile && classes.rootMobile,
      )}
      style={{
        top: position.top,
        left: position.left !== 'auto' ? position.left : undefined,
        right: position.right !== 'auto' ? position.right : undefined,
        bottom: position.bottom !== 'auto' ? position.bottom : undefined,
      }}
      elevation={0}
    >
      <Box
        className={cx(classes.header, isCollapsed && classes.headerCollapsed)}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        <Box className={classes.headerLeft}>
          <DragIndicatorIcon className={classes.dragHandle} />
          <Box className={classes.headerTitle}>
            {icon}
            <Typography variant="subtitle2" className={classes.titleText}>
              {title}
            </Typography>
          </Box>
        </Box>
        <Box className={classes.headerActions}>
          {count && (
            <Chip
              label={count}
              size="small"
              color={countColor}
              className={classes.countChip}
            />
          )}
          <Tooltip title={isCollapsed ? 'Expandir' : 'Recolher'}>
            <IconButton
              size="small"
              onClick={handleToggleCollapse}
              className={cx(classes.iconButton, classes.collapseButton)}
            >
              {isCollapsed ? (
                <ExpandMoreIcon fontSize="small" />
              ) : (
                <ExpandLessIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Fechar">
            <IconButton
              size="small"
              onClick={onClose}
              className={cx(classes.iconButton, classes.closeButton)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Collapse in={!isCollapsed} timeout={200}>
        <Box className={classes.content}>
          {children}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default DraggableFilterPanel;
